/**
 * Shared builders for OIG adverse-check Excel export rows.
 * Used by results-page download and history persist so both stay aligned (#10262 / #10333).
 */

export type OigExportLicense = { number?: string };

export type OigExportUser = {
  name?: string;
  npi?: string;
  phoneNumber?: string;
  ssn?: string;
  licenses?: OigExportLicense[];
  /** Bulk roster Type column (Employee / Vendor); empty for single checks. */
  checkType?: string;
};

export type OigExportResult = {
  data?: Record<string, unknown> | null;
  total_exclusions?: number;
  success?: boolean;
};

/** Stable Results-sheet column order — must match rules `OIG_EXPORT_RESULT_HEADERS`. */
export const OIG_EXPORT_RESULT_HEADERS = [
  "User Name",
  "NPI",
  "SSN",
  "Phone",
  "Licenses",
  "Agency",
  "Match Score",
  "Status",
  "Exclusion Count",
  "Details",
  "Type",
] as const;

export type OigExportRow = {
  "User Name": string;
  NPI: string;
  SSN: string;
  Phone: string;
  Licenses: string;
  Agency: string;
  "Match Score": string | number;
  Status: string;
  "Exclusion Count": number;
  Details: string;
  Type: string;
};

/**
 * Build AOA for Excel with canonical header order (#10333).
 * Unknown keys (if any) are appended sorted after the canonical set.
 */
export function exportRowsToAoa(
  rows: Array<Record<string, unknown>>,
): (string | number)[][] {
  const present = new Set<string>();
  for (const row of rows) {
    Object.keys(row).forEach((k) => present.add(k));
  }
  const canonical = OIG_EXPORT_RESULT_HEADERS as readonly string[];
  const extras = [...present].filter((k) => !canonical.includes(k)).sort();
  const headers = [...canonical, ...extras];
  return [
    headers,
    ...rows.map((row) =>
      headers.map((h) => {
        const v = row[h];
        if (v === undefined || v === null) return "";
        return v as string | number;
      }),
    ),
  ];
}

/** Mask SSN to ***-**-XXXX when at least 4 digits present. */
export function maskSsnLast4(ssn?: string | null): string {
  if (!ssn) return "";
  const digits = String(ssn).replace(/\D/g, "");
  if (digits.length < 4) return "";
  return `***-**-${digits.slice(-4)}`;
}

type RawExclusion = Record<string, unknown>;

function itemRoot(item: Record<string, unknown>): Record<string, unknown> {
  const professional = item.professional;
  if (professional && typeof professional === "object" && !Array.isArray(professional)) {
    return professional as Record<string, unknown>;
  }
  return item;
}

function exclusionsFromItem(item: Record<string, unknown>): RawExclusion[] {
  const root = itemRoot(item);
  const attrs = root.attributes;
  if (!attrs || typeof attrs !== "object" || Array.isArray(attrs)) return [];
  const nested = (attrs as Record<string, unknown>).exclusions;
  if (!Array.isArray(nested)) return [];
  return nested.filter(
    (ex): ex is RawExclusion => !!ex && typeof ex === "object" && !Array.isArray(ex),
  );
}

/** Stable key for dropping duplicate exclusion rows in results export. */
export function exclusionExportDedupeKey(ex: RawExclusion): string {
  const profession = String(ex.profession ?? "").trim().toLowerCase();
  const excludedDate = String(ex.excludedDate ?? "").trim();
  const listCode = String(ex.listCode ?? ex.exclusionsType ?? "").trim().toLowerCase();
  const license = String(ex.licenseNumber ?? "").trim();
  const agency = String(ex.agency ?? ex.source ?? ex.excludingAgency ?? "").trim().toLowerCase();
  const description = String(ex.description ?? ex.details ?? "").trim().toLowerCase();
  return [profession, excludedDate, listCode, license, agency, description].join("|");
}

export function dedupeExclusionsForExport(exclusions: RawExclusion[]): RawExclusion[] {
  const seen = new Set<string>();
  const out: RawExclusion[] = [];
  for (const ex of exclusions) {
    const key = exclusionExportDedupeKey(ex);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(ex);
  }
  return out;
}

function flattenExclusions(data: Record<string, unknown>): RawExclusion[] {
  if (Array.isArray(data.exclusions)) {
    return (data.exclusions as unknown[]).filter(
      (ex): ex is RawExclusion => !!ex && typeof ex === "object" && !Array.isArray(ex),
    );
  }
  const items = data.items;
  if (!Array.isArray(items)) return [];
  const out: RawExclusion[] = [];
  for (const raw of items) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) continue;
    const item = raw as Record<string, unknown>;
    const root = itemRoot(item);
    const profScore = root.score ?? root.match_score ?? item.score ?? item.match_score;
    for (const ex of exclusionsFromItem(item)) {
      out.push({
        agency:
          (ex.agency as string) ||
          (ex.source as string) ||
          (ex.excludingAgency as string) ||
          "",
        match_score: profScore,
        status: (ex.status as string) || (ex.state as string) || "",
        profession: ex.profession,
        excludedDate: ex.excludedDate,
        listCode: ex.listCode ?? ex.exclusionsType,
        licenseNumber: ex.licenseNumber,
        description:
          (ex.description as string) ||
          `Profession: ${ex.profession || ""}; Excluded: ${
            ex.excludedDate || ""
          }; List Code: ${ex.listCode || ex.exclusionsType || ""}; License: ${
            ex.licenseNumber || ""
          }`,
        details: ex.details,
      });
    }
  }
  return out;
}

export type FormatSsnVerificationLabel = (
  data: Record<string, unknown>,
) => string | null | undefined;

/**
 * Build Excel rows for one adverse-check result (one row per unique exclusion).
 */
export function buildRowsForResult(
  res: OigExportResult,
  user?: OigExportUser,
  formatSsnVerificationLabel?: FormatSsnVerificationLabel,
): OigExportRow[] {
  const rows: OigExportRow[] = [];
  const name = user?.name || "";
  const data = (res?.data || {}) as Record<string, unknown>;
  const npi = user?.npi || (typeof data.npi === "string" ? data.npi : "") || "";
  const phone = user?.phoneNumber || "";
  const licenses = Array.isArray(user?.licenses)
    ? user?.licenses.map((l) => l.number).filter(Boolean).join(", ") ?? ""
    : "";

  const exclusions = dedupeExclusionsForExport(flattenExclusions(data));

  const exclusionCount: number =
    (typeof data.total_exclusions === "number"
      ? data.total_exclusions
      : typeof res?.total_exclusions === "number"
        ? res.total_exclusions
        : exclusions.length) || 0;

  let userStatus = "Not found";
  if (typeof data.total_exclusions === "number") {
    userStatus = data.total_exclusions > 0 ? "Exclusion Found" : "Verified";
    if (formatSsnVerificationLabel) {
      const ssnLabel = formatSsnVerificationLabel(data);
      if (ssnLabel) {
        userStatus = `${userStatus} (${ssnLabel})`;
      }
    }
  }

  const ssnLast4 = maskSsnLast4(user?.ssn);
  const rowType = user?.checkType || "";
  // After dedupe, prefer unique row count when API total_exclusions over-counts duplicates.
  const rowExclusionCount =
    exclusions.length > 0 ? exclusions.length : exclusionCount;

  if (exclusions.length > 0) {
    for (const ex of exclusions) {
      rows.push({
        "User Name": name,
        NPI: npi,
        SSN: ssnLast4,
        Phone: phone,
        Licenses: licenses,
        Agency: String(ex.agency || ex.source || ""),
        "Match Score": (ex.match_score ?? ex.score ?? "") as string | number,
        Status: userStatus,
        "Exclusion Count": rowExclusionCount,
        Details: String(ex.details || ex.description || ""),
        Type: rowType,
      });
    }
  } else {
    rows.push({
      "User Name": name,
      NPI: npi,
      SSN: ssnLast4,
      Phone: phone,
      Licenses: licenses,
      Agency: "",
      "Match Score": "",
      Status: userStatus,
      "Exclusion Count": exclusionCount,
      Details:
        exclusionCount > 0
          ? "Exclusion details unavailable in export"
          : "No exclusion records found",
      Type: rowType,
    });
  }
  return rows;
}
